
import React from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

const BioLinkProfile = ({ user, dashboardBanner }) => {
    const displayName =
        user?.full_name ||
        user?.name ||
        [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
        user?.username ||
        "Musu Flomo";

    return (
        <>
            <style>
                {`
                    @keyframes gradientFlow {
                        0% {
                            background-position: 0% 50%;
                        }
                        50% {
                            background-position: 100% 50%;
                        }
                        100% {
                            background-position: 0% 50%;
                        }
                    }

                    @keyframes lightMovement {
                        0% {
                            transform: translate(-20%, -10%) scale(1);
                        }
                        50% {
                            transform: translate(20%, 15%) scale(1.25);
                        }
                        100% {
                            transform: translate(-20%, -10%) scale(1);
                        }
                    }

                    @keyframes goldMovement {
                        0% {
                            transform: translateX(-110%) rotate(15deg);
                        }
                        100% {
                            transform: translateX(450%) rotate(15deg);
                        }
                    }

                    @keyframes glowPulse {
                        0%, 100% {
                            opacity: 0.25;
                            transform: scale(1);
                        }
                        50% {
                            opacity: 0.65;
                            transform: scale(1.18);
                        }
                    }

                    .welcome-banner {
                        isolation: isolate;
                    }

                    .welcome-gradient {
                        position: absolute;
                        inset: 0;
                        z-index: -1;
                        background: linear-gradient(
                            115deg,
                            rgba(2, 20, 38, 0.58),
                            rgba(3, 27, 53, 0.43),
                            rgba(6, 58, 107, 0.32),
                            rgba(2, 20, 38, 0.52)
                        );
                        background-size: 300% 300%;
                        animation: gradientFlow 12s ease infinite;
                    }

                    .welcome-light {
                        position: absolute;
                        width: 420px;
                        height: 420px;
                        border-radius: 50%;
                        background: radial-gradient(
                            circle,
                            rgba(15, 109, 171, 0.35) 0%,
                            rgba(6, 58, 107, 0.16) 40%,
                            transparent 72%
                        );
                        top: -220px;
                        right: -100px;
                        pointer-events: none;
                        animation: lightMovement 10s ease-in-out infinite;
                    }

                    .welcome-gold-glow {
                        position: absolute;
                        width: 300px;
                        height: 300px;
                        border-radius: 50%;
                        background: radial-gradient(
                            circle,
                            rgba(246, 201, 68, 0.20),
                            transparent 70%
                        );
                        bottom: -190px;
                        left: 35%;
                        pointer-events: none;
                        animation: glowPulse 7s ease-in-out infinite;
                    }

                    .welcome-shimmer {
                        position: absolute;
                        top: -30%;
                        left: -30%;
                        width: 90px;
                        height: 170%;
                        background: linear-gradient(
                            90deg,
                            transparent,
                            rgba(255,255,255,0.09),
                            rgba(246,201,68,0.13),
                            transparent
                        );
                        transform: rotate(15deg);
                        animation: goldMovement 11s ease-in-out infinite;
                        pointer-events: none;
                    }

                    .welcome-top-line {
                        background: linear-gradient(
                            90deg,
                            #C0750D,
                            #F6C944,
                            #FFE58A,
                            #FFFFFF,
                            #F6C944,
                            #C0750D
                        );
                        background-size: 250% 100%;
                        animation: gradientFlow 6s linear infinite;
                    }

                    @media (prefers-reduced-motion: reduce) {
                        .welcome-gradient,
                        .welcome-light,
                        .welcome-gold-glow,
                        .welcome-shimmer,
                        .welcome-top-line {
                            animation: none !important;
                        }
                    }
                `}
            </style>

            <motion.div
                initial={{ opacity: 0, y: 22 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.75, ease: "easeOut" }}
            >
                <Box
                    className="welcome-banner"
                    sx={{
                        height: {
                            xs: 190,
                            sm: 250,
                            md: 320,
                        },
                        position: "relative",
                        overflow: "hidden",
                        borderRadius: {
                            xs: 3,
                            md: 4,
                        },
                        backgroundColor: "#031B35",
                        boxShadow:
                            "0 18px 45px rgba(3,27,53,0.16)",
                    }}
                >
                    {/* Original background image - kept bright and visible */}
                    {dashboardBanner && (
                        <Box
                            component="img"
                            src={dashboardBanner}
                            alt="School dashboard"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                                zIndex: -2,
                                filter: "brightness(0.88) saturate(1.08)",
                                transition: "transform 1.2s ease",
                                transform: "scale(1.01)",
                            }}
                        />
                    )}

                    {/* Transparent animated gradient overlay */}
                    <Box className="welcome-gradient" />

                    {/* Moving blue light */}
                    <Box className="welcome-light" />

                    {/* Gold ambient glow */}
                    <Box className="welcome-gold-glow" />

                    {/* Moving light shimmer */}
                    <Box className="welcome-shimmer" />

                    {/* Animated gold top border */}
                    <Box
                        className="welcome-top-line"
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "4px",
                            zIndex: 5,
                        }}
                    />

                    {/* Additional subtle texture */}
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            backgroundImage: `
                                radial-gradient(
                                    circle at 75% 25%,
                                    rgba(255,255,255,0.10) 0,
                                    transparent 32%
                                ),
                                linear-gradient(
                                    135deg,
                                    rgba(255,255,255,0.035),
                                    transparent 50%
                                )
                            `,
                        }}
                    />

                    {/* Welcome text */}
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            px: {
                                xs: 2.5,
                                sm: 4,
                                md: 7,
                            },
                            py: 3,
                            zIndex: 3,
                        }}
                    >
                        <Box sx={{ maxWidth: 720 }}>
                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                    delay: 0.2,
                                    duration: 0.8,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: "#FFE58A",
                                        fontWeight: 800,
                                        fontSize: {
                                            xs: 10,
                                            sm: 12,
                                            md: 13,
                                        },
                                        letterSpacing: {
                                            xs: 1.5,
                                            md: 3,
                                        },
                                        mb: {
                                            xs: 1,
                                            md: 1.5,
                                        },
                                        textShadow:
                                            "0 2px 8px rgba(0,0,0,0.35)",
                                    }}
                                >
                                    STUDENT DASHBOARD
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "#FFFFFF",
                                        fontWeight: 900,
                                        fontSize: {
                                            xs: 23,
                                            sm: 32,
                                            md: 43,
                                        },
                                        lineHeight: 1.2,
                                        letterSpacing: -0.8,
                                        textShadow:
                                            "0 3px 16px rgba(0,0,0,0.55)",
                                    }}
                                >
                                    Welcome Back,
                                    <Box
                                        component="span"
                                        sx={{
                                            color: "#F6C944",
                                            ml: 1,
                                        }}
                                    >
                                        {displayName}
                                    </Box>
                                </Typography>

                                <Typography
                                    sx={{
                                        color: "#FFFFFF",
                                        fontSize: {
                                            xs: 11,
                                            sm: 14,
                                            md: 17,
                                        },
                                        fontWeight: 500,
                                        lineHeight: 1.7,
                                        mt: {
                                            xs: 1,
                                            md: 1.5,
                                        },
                                        maxWidth: 580,
                                        textShadow:
                                            "0 2px 9px rgba(0,0,0,0.65)",
                                    }}
                                >
                                    Manage your tasks, academic progress,
                                    attendance and school activities.
                                </Typography>

                                <Box
                                    sx={{
                                        width: {
                                            xs: 48,
                                            md: 68,
                                        },
                                        height: 4,
                                        borderRadius: 5,
                                        background:
                                            "linear-gradient(90deg, #C0750D, #F6C944, #FFE58A)",
                                        mt: {
                                            xs: 2,
                                            md: 2.5,
                                        },
                                        boxShadow:
                                            "0 0 15px rgba(246,201,68,0.55)",
                                    }}
                                />
                            </motion.div>
                        </Box>
                    </Box>

                    {/* Decorative rings */}
                    <Box
                        sx={{
                            position: "absolute",
                            width: {
                                xs: 110,
                                md: 200,
                            },
                            height: {
                                xs: 110,
                                md: 200,
                            },
                            border: "1px solid rgba(246,201,68,0.28)",
                            borderRadius: "50%",
                            right: {
                                xs: -45,
                                md: 55,
                            },
                            bottom: {
                                xs: -65,
                                md: -100,
                            },
                            pointerEvents: "none",
                        }}
                    />

                    <Box
                        sx={{
                            position: "absolute",
                            width: {
                                xs: 75,
                                md: 135,
                            },
                            height: {
                                xs: 75,
                                md: 135,
                            },
                            border: "1px solid rgba(255,255,255,0.17)",
                            borderRadius: "50%",
                            right: {
                                xs: -28,
                                md: 87,
                            },
                            bottom: {
                                xs: -48,
                                md: -68,
                            },
                            pointerEvents: "none",
                        }}
                    />
                </Box>
            </motion.div>
        </>
    );
};

export default BioLinkProfile;
